import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';

interface SEOHeadProps {
  /** Translation key prefix for this page, e.g. 'home', 'products' */
  page: string;
}

const BASE_URL = 'https://gevelsteenbedrijf.be';

/**
 * Sets document title, meta description, Open Graph tags,
 * html lang attribute, keywords, canonical URL, and JSON-LD
 * structured data based on current language.
 */
export default function SEOHead({ page }: SEOHeadProps) {
  const { lang, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const title = t(`seo.${page}.title`);
    const description = t(`seo.${page}.description`);
    const keywords = t(`seo.${page}.keywords`);

    // Document title
    document.title = title;

    // HTML lang attribute (BCP-47 region tags help Belgian SEO)
    const htmlLang = lang === 'fr' ? 'fr-BE' : lang === 'en' ? 'en' : 'nl-BE';
    document.documentElement.lang = htmlLang;

    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    // Helper to set or create a link tag
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Canonical URL
    const canonicalPath = location.pathname === '/' ? '' : location.pathname;
    setLink('canonical', `${BASE_URL}${canonicalPath}`);

    // hreflang alternates — critical for multi-region SEO so Google serves
    // the right language version in NL/FR/EN search results.
    const setHreflang = (hreflang: string, href: string) => {
      let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    // Different hosts/paths could be used per language; for now we keep the same
    // URL with a `?lang=` hint for crawlers. Update when language-prefixed
    // routes are introduced (e.g. /fr/, /en/).
    setHreflang('nl-BE', `${BASE_URL}${canonicalPath}`);
    setHreflang('fr-BE', `${BASE_URL}${canonicalPath}?lang=fr`);
    setHreflang('en',    `${BASE_URL}${canonicalPath}?lang=en`);
    setHreflang('x-default', `${BASE_URL}${canonicalPath}`);

    // Standard meta tags
    setMeta('name', 'description', description);
    if (keywords && !keywords.startsWith('seo.')) {
      setMeta('name', 'keywords', keywords);
    }
    setMeta('name', 'language', lang === 'fr' ? 'fr' : lang === 'en' ? 'en' : 'nl');

    // Open Graph tags
    const ogLocale = lang === 'fr' ? 'fr_BE' : lang === 'en' ? 'en_US' : 'nl_BE';
    const ogAlternates = (['nl_BE', 'fr_BE', 'en_US'] as const).filter(l => l !== ogLocale);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'Gevelsteenbedrijf.be');
    setMeta('property', 'og:locale', ogLocale);
    // Multiple og:locale:alternate are allowed — set the first as a representative
    setMeta('property', 'og:locale:alternate', ogAlternates[0]);
    setMeta('property', 'og:url', `${BASE_URL}${canonicalPath}`);

    // Per-page OG image (falls back to base og-image for pages without their own).
    // Lets Facebook/LinkedIn/WhatsApp show a contextual preview for each page.
    // SVG works for Google Rich Results, Twitter X, and most modern crawlers.
    // For full Facebook/LinkedIn raster compatibility, convert to JPG offline.
    const pageImage: Record<string, string> = {
      home:          `${BASE_URL}/og-image.svg`,
      products:      `${BASE_URL}/og-image.svg`,
      inspiration:   `${BASE_URL}/og-inspiratie.svg`,
      custom:        `${BASE_URL}/og-custom.svg`,
      architects:    `${BASE_URL}/og-custom.svg`,
      restpartijen:  `${BASE_URL}/og-restpartijen.svg`,
      contact:       `${BASE_URL}/og-contact.svg`,
    };
    const ogImage = pageImage[page] || `${BASE_URL}/og-image.svg`;
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:type', 'image/svg+xml');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', title);

    // Robots — explicit per-page directive (Google honours these for indexing decisions).
    // Login/dashboard pages set noindex via the page itself; for everything else: rich preview allowed.
    const robotsByPage: Record<string, string> = {
      login:        'noindex, nofollow',
      register:     'noindex, nofollow',
      dashboard:    'noindex, nofollow',
    };
    setMeta('name', 'robots', robotsByPage[page] || 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Twitter Card tags
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // Geo tags for Belgian business
    setMeta('name', 'geo.region', 'BE-VAN'); // Antwerp province
    setMeta('name', 'geo.placename',
      lang === 'fr' ? 'Schilde, Belgique' :
      lang === 'en' ? 'Schilde, Belgium' :
      'Schilde, België'
    );
    setMeta('name', 'geo.position', '51.2425;4.5856'); // Schilde lat/long
    setMeta('name', 'ICBM', '51.2425, 4.5856');

    // JSON-LD Structured Data
    const existingJsonLd = document.querySelector('script[data-jsonld="seo"]');
    if (existingJsonLd) existingJsonLd.remove();

    const jsonLdScript = document.createElement('script');
    jsonLdScript.type = 'application/ld+json';
    jsonLdScript.setAttribute('data-jsonld', 'seo');

    const orgDescription =
      lang === 'fr' ? 'Plaquettes de parement premium directement de l\'usine. Économisez jusqu\'à 40%.' :
      lang === 'en' ? 'Premium brick strips direct from the manufacturer. Save up to 40%.' :
      'Premium baksteenstrips rechtstreeks van fabrikant. Bespaar tot 40%.';

    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${BASE_URL}/#organization`,
      name: 'Gevelsteenbedrijf.be',
      url: BASE_URL,
      logo: `${BASE_URL}/logo.svg`,
      description: orgDescription,
      telephone: '+32 (0)3 123 45 67',
      email: 'info@gevelsteenbedrijf.be',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Bredabaan 123',
        postalCode: '2970',
        addressLocality: 'Schilde',
        addressRegion: lang === 'fr' ? 'Anvers' : lang === 'en' ? 'Antwerp' : 'Antwerpen',
        addressCountry: 'BE',
      },
      vatID: 'BE0123.456.789',
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 51.2425,
        longitude: 4.5856,
      },
      areaServed: {
        '@type': 'Country',
        name: lang === 'fr' ? 'Belgique' : lang === 'en' ? 'Belgium' : 'België',
      },
      priceRange: '€€',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '17:00',
      },
      sameAs: [],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: `${BASE_URL}${canonicalPath}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Gevelsteenbedrijf.be',
        url: BASE_URL,
      },
      inLanguage: htmlLang,
    };

    // Product schema only for product-related pages
    const schemas: object[] = [orgSchema, webPageSchema];

    if (page === 'products' || page === 'home') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: lang === 'fr' ? 'Plaquettes de Parement Premium' : 'Premium Baksteenstrips',
        description: lang === 'fr'
          ? 'Plaquettes de parement premium direct d\'usine, disponibles en plusieurs couleurs et textures.'
          : 'Premium baksteenstrips direct van fabrikant, beschikbaar in diverse kleuren en texturen.',
        brand: {
          '@type': 'Brand',
          name: 'Gevelsteenbedrijf.be',
        },
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: '29.50',
          highPrice: '54.90',
          offerCount: '6',
          availability: 'https://schema.org/InStock',
        },
        category: lang === 'fr' ? 'Revêtement de façade' : 'Gevelbekleding',
      });
    }

    if (page === 'inspiration') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'ImageGallery',
        name: title,
        description: description,
        url: `${BASE_URL}/inspiratie`,
        about: {
          '@type': 'Thing',
          name: lang === 'fr' ? 'Façades en plaquettes de parement'
              : lang === 'en' ? 'Brick strip facades'
              : 'Baksteenstrip gevels',
        },
        keywords: lang === 'fr'
          ? 'façade moderne, façade rustique, façade industrielle, façade classique, rénovation façade'
          : lang === 'en'
            ? 'modern facade, rustic facade, industrial facade, classic facade, facade renovation'
            : 'moderne gevel, landelijke gevel, industriële gevel, klassieke gevel, gevelrenovatie',
      });
      // CollectionPage — signals that this page lists multiple items
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${BASE_URL}/inspiratie#collection`,
        name: title,
        url: `${BASE_URL}/inspiratie`,
        inLanguage: htmlLang,
        isPartOf: { '@type': 'WebSite', name: 'Gevelsteenbedrijf.be', url: BASE_URL },
      });
    }

    // ─── Custom / Professioneel page (B2B custom production) ────────────────
    if (page === 'custom' || page === 'architects') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${BASE_URL}/professioneel#service`,
        serviceType: lang === 'fr'
          ? 'Production sur mesure de plaquettes de parement'
          : lang === 'en'
            ? 'Custom brick strip production'
            : 'Custom productie van baksteenstrips op maat',
        name: title,
        description: description,
        url: `${BASE_URL}/professioneel`,
        provider: { '@id': `${BASE_URL}/#organization` },
        areaServed: { '@type': 'Country', name: lang === 'fr' ? 'Belgique' : lang === 'en' ? 'Belgium' : 'België' },
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          eligibleQuantity: { '@type': 'QuantitativeValue', minValue: 100, unitCode: 'MTK' /* m² */ },
          availability: 'https://schema.org/InStock',
          deliveryLeadTime: { '@type': 'QuantitativeValue', minValue: 6, maxValue: 8, unitCode: 'WEE' },
        },
        audience: {
          '@type': 'BusinessAudience',
          audienceType: lang === 'fr'
            ? 'Architectes, entrepreneurs, promoteurs immobiliers'
            : lang === 'en'
              ? 'Architects, contractors, property developers'
              : 'Architecten, aannemers, projectontwikkelaars',
        },
      });
    }

    // ─── Restpartijen / overstock page (sale items) ─────────────────────────
    if (page === 'restpartijen') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${BASE_URL}/restpartijen#collection`,
        name: title,
        description: description,
        url: `${BASE_URL}/restpartijen`,
        inLanguage: htmlLang,
        isPartOf: { '@type': 'WebSite', name: 'Gevelsteenbedrijf.be', url: BASE_URL },
        about: {
          '@type': 'Thing',
          name: lang === 'fr' ? 'Plaquettes de parement en fin de stock'
              : lang === 'en' ? 'End-of-stock brick strips'
              : 'Restpartijen baksteenstrips',
        },
      });
      // AggregateOffer — signals to Google that this page lists discounted items
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'OfferCatalog',
        name: title,
        url: `${BASE_URL}/restpartijen`,
        itemListElement: [{
          '@type': 'Offer',
          name: lang === 'fr'
            ? 'Plaquettes de parement à prix réduit'
            : lang === 'en'
              ? 'Discounted brick strips'
              : 'Baksteenstrips met korting',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'EUR',
            valueAddedTaxIncluded: true,
          },
          availability: 'https://schema.org/LimitedAvailability',
          eligibleRegion: { '@type': 'Country', name: 'BE' },
        }],
      });
    }

    // ─── Contact page (ContactPage + ContactPoint) ──────────────────────────
    if (page === 'contact') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': `${BASE_URL}/contact#contactpage`,
        name: title,
        description: description,
        url: `${BASE_URL}/contact`,
        inLanguage: htmlLang,
        isPartOf: { '@type': 'WebSite', name: 'Gevelsteenbedrijf.be', url: BASE_URL },
        mainEntity: {
          '@type': 'Organization',
          '@id': `${BASE_URL}/#organization`,
          contactPoint: [{
            '@type': 'ContactPoint',
            contactType: 'customer service',
            telephone: '+32 (0)3 123 45 67',
            email: 'info@gevelsteenbedrijf.be',
            areaServed: 'BE',
            availableLanguage: ['Dutch', 'French', 'English'],
            hoursAvailable: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '08:00',
              closes: '17:00',
            },
          }, {
            '@type': 'ContactPoint',
            contactType: 'sales',
            email: 'verkoop@gevelsteenbedrijf.be',
            areaServed: 'BE',
            availableLanguage: ['Dutch', 'French'],
          }],
        },
      });
    }

    // BreadcrumbList — helps Google show the navigation hierarchy in search results.
    const homeLabel = lang === 'fr' ? 'Accueil' : lang === 'en' ? 'Home' : 'Home';
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length > 0) {
      const breadcrumbs: { '@type': string; position: number; name: string; item: string }[] = [
        { '@type': 'ListItem', position: 1, name: homeLabel, item: `${BASE_URL}/` },
      ];
      let cumulative = '';
      segments.forEach((seg, i) => {
        cumulative += `/${seg}`;
        // Pretty-name: capitalise first letter, replace dashes
        const niceName = seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ');
        breadcrumbs.push({
          '@type': 'ListItem',
          position: i + 2,
          name: niceName,
          item: `${BASE_URL}${cumulative}`,
        });
      });
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
      });
    }

    jsonLdScript.textContent = JSON.stringify(schemas);
    document.head.appendChild(jsonLdScript);

    return () => {
      const script = document.querySelector('script[data-jsonld="seo"]');
      if (script) script.remove();
    };
  }, [lang, page, t, location.pathname]);

  return null;
}
